import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from "framer-motion";

const roadmapData = [
    {
      quarter: "Q2 2025",
      title: "ARENA token launch",
      items: [
        {
          text: "Beta: 5m/1h/24h contests",
          status: "done"  // Options: "done", "inProgress", "planned"
        },
        {
          text: "Referral program",
          status: "done"
        },
        {
            text: "Token integration into gameplay",
            status: "inProgress"
          }
      ],
      color: "green"
    },
    {
      quarter: "Q3 2025",
      title: "Gameplay enhancements",
      items: [
        {
          text: "AI model refinements",
          status: "inProgress"
        },
        {
          text: "Smart contracts live",
          status: "planned"
        },
        {
          text: "Leaderboard expansions",
          status: "done"
        }
      ],
      color: "blue"
    },
    {
      quarter: "Q4 2025",
      title: "Platform expansion",
      items: [
        {
          text: "Mobile app launch",
          status: "planned"
        },
        {
          text: "New options-style bets",
          status: "planned"
        },
        {
          text: "Initial governance features",
          status: "planned"
        }
      ],
      color: "purple"
    },
    {
      quarter: "Q1 2026",
      title: "DAO and multi-chain",
      items: [
        {
          text: "Cross-chain market support",
          status: "planned"
        },
        {
          text: "Advanced derivatives",
          status: "planned"
        },
        {
          text: "Full DAO governance rollout",
          status: "planned"
        }
      ],
      color: "yellow"
    }
  ];

export default function RoadmapCard() {
  const [activeQuarter, setActiveQuarter] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const timelineRef = useRef(null);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' or 'right'

  // Handle viewport width changes
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Scroll timeline on mobile when changing quarters
  useEffect(() => {
    if (isMobile && timelineRef.current) {
      const scrollPosition = activeQuarter * 100; // Approximate position
      timelineRef.current.scrollLeft = scrollPosition;
    }
  }, [activeQuarter, isMobile]);

  const getColor = (color) => {
    switch(color) {
      case "green": return "from-green-400 to-green-600";
      case "blue": return "from-blue-400 to-blue-600";
      case "purple": return "from-purple-400 to-purple-600";
      case "yellow": return "from-yellow-400 to-yellow-600";
      default: return "from-violet-400 to-pink-400";
    }
  };

  const getLineColor = (color) => {
    switch(color) {
      case "green": return "border-green-500";
      case "blue": return "border-blue-500";
      case "purple": return "border-purple-500";
      case "yellow": return "border-yellow-500";
      default: return "border-pink-500";
    }
  };

  // Simple touch handling
  const touchStartX = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchEndX - touchStartX.current;
    
    // Only register swipes with sufficient movement
    if (Math.abs(diff) < 50) return;
    
    if (diff > 0) {
      // Swiped right (finger moved right)
      if (activeQuarter > 0) {
        setSwipeDirection('right');
        setActiveQuarter(prev => prev - 1);
      }
    } else {
      // Swiped left (finger moved left)
      if (activeQuarter < roadmapData.length - 1) {
        setSwipeDirection('left');
        setActiveQuarter(prev => prev + 1);
      }
    }
    
    touchStartX.current = null;
  };

  // Handle button navigation
  const goToPrev = () => {
    if (activeQuarter > 0) {
      setSwipeDirection('right');
      setActiveQuarter(prev => prev - 1);
    }
  };

  const goToNext = () => {
    if (activeQuarter < roadmapData.length - 1) {
      setSwipeDirection('left');
      setActiveQuarter(prev => prev + 1);
    }
  };

  // Animation variants
  const variants = {
    enter: (direction) => {
      return {
        x: direction === 'left' ? 300 : -300,
        opacity: 0
      };
    },
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => {
      return {
        x: direction === 'left' ? -300 : 300,
        opacity: 0
      };
    }
  };

  return (
    <section id="roadmap" className="-mt-4 my-8 font-mono">
      {/* Section title */}
      <div className="flex items-center mb-4 md:mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
          Roadmap
        </h3>
        <div className="flex-grow h-px ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent"></div>
        <div className="ml-2 px-2 md:px-3 py-1 bg-black bg-opacity-40 rounded text-xs md:text-sm text-pink-400 border border-pink-500/30">
          What's Next
        </div>
      </div>

      {/* Gradient border wrapper */}
      <div className="animate-gradient-border mb-12 pt-4 mt-8 p-0.5 rounded-3xl">
        {/* Inner dark card */}
        <div className="bg-gray-900/90 backdrop-blur-sm rounded-3xl p-6 sm:p-8 shadow-lg overflow-hidden">
          
          {/* Touch event wrapper */}
          <div 
            className="touch-wrapper"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <AnimatePresence custom={swipeDirection} mode="wait">
              <motion.div
                key={activeQuarter}
                custom={swipeDirection}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="mt-4"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
                  {/* Left side: Title and description */}
                  <div className="lg:col-span-4 mb-4 lg:mb-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4
                        className={`text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r ${getColor(
                          roadmapData[activeQuarter].color
                        )}`}
                      >
                        {roadmapData[activeQuarter].quarter}
                      </h4>
                      {/* Mobile nav buttons */}
                      <div className="flex gap-2 lg:hidden">
                        <button
                          onClick={goToPrev}
                          disabled={activeQuarter === 0}
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            activeQuarter === 0
                              ? "bg-gray-800 text-gray-600"
                              : "bg-gray-800 text-white"
                          }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={goToNext}
                          disabled={activeQuarter === roadmapData.length - 1}
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            activeQuarter === roadmapData.length - 1
                              ? "bg-gray-800 text-gray-600"
                              : "bg-gray-800 text-white"
                          }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                      {roadmapData[activeQuarter].title}
                    </h3>
                    <div
                      className={`h-1 w-16 rounded-full bg-gradient-to-r ${getColor(
                        roadmapData[activeQuarter].color
                      )} mb-3`}
                    ></div>
                    <p className="text-gray-400 text-sm md:text-base">
                      Our focus for this quarter is to deliver key milestones that will
                      strengthen our platform and expand our ecosystem.
                    </p>
                  </div>

                  {/* Right side: Feature list */}
                  <div className="lg:col-span-8">
                    <div className="space-y-5">
                      {roadmapData[activeQuarter].items.map((item, idx) => (
                        <div key={idx} className="flex items-center">
                          {/* Status indicator */}
                          <div className="mr-4 flex-shrink-0">
                            {item.status === "done" ? (
                              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            ) : item.status === "inProgress" ? (
                              <div className="w-5 h-5 rounded-full border-2 border-orange-400 flex items-center justify-center">
                                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-gray-400"></div>
                            )}
                          </div>
                          <span
                            className={`font-sans text-base flex-grow tracking-tight ${
                              item.status === "done"
                                ? "text-green-400 opacity-70"
                                : item.status === "inProgress"
                                ? "text-orange-400 opacity-70"
                                : "text-white-400 opacity-70"
                            }`}
                          >
                            {item.text}
                          </span>
                          <span
                            className={`text-xs ${
                              idx === 0
                                ? "text-rose-400"
                                : idx === 1
                                ? "text-amber-400"
                                : "text-emerald-400"
                            } font-mono`}
                          >
                            {idx === 0 ? "CRITICAL" : idx === 1 ? "HIGH" : "CORE"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-6 md:mt-8">
                  <div className="flex justify-between text-xs md:text-sm text-gray-500 mb-2">
                    <span>Progress</span>
                    <span>{25 * (activeQuarter + 1)}%</span>
                  </div>
                  <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${getColor(
                        roadmapData[activeQuarter].color
                      )} transition-all duration-500`}
                      style={{ width: `${25 * (activeQuarter + 1)}%` }}
                    ></div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .animate-gradient-border {
          background: linear-gradient(90deg, #8b5cf6, #ec4899, #8b5cf6);
          background-size: 200% 200%;
          animation: gradient 6s ease infinite;
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 0.4; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </section>
  );
}
